// "Add field -> E-mail details": pick details of the e-mail a request came
// from (sender, subject, ...) and each becomes a finished template field in one
// click. Nothing to configure: the backend sets name, type, description and
// the "E-mail" section, and these fields are never read by AI.
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Loader2, Mail, X } from 'lucide-react'
import { EMAIL_FIELD_OPTIONS } from '../utils/fieldValueSources'

export default function EmailFieldsPicker({ open, onClose, onAdd, existingSources, saving }) {
  const [selected, setSelected] = useState(() => new Set())

  useEffect(() => {
    if (open) setSelected(new Set())
  }, [open])

  if (!open) return null

  const toggle = (source) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(source)) next.delete(source)
      else next.add(source)
      return next
    })

  // Keep the picker's order, whatever order the chips were clicked in.
  const chosen = EMAIL_FIELD_OPTIONS.map((o) => o.source).filter((s) => selected.has(s))

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-fields-title"
        className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="email-fields-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Mail className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              Add e-mail details
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Copied from the e-mail each request came from (its sender, subject, date…), not read by AI from its text or attachments. Pick what you need; nothing to set up.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {EMAIL_FIELD_OPTIONS.map(({ source, label, example }) => {
            const added = existingSources.has(source)
            const isSelected = selected.has(source)
            return (
              <button
                key={source}
                type="button"
                aria-pressed={added || isSelected}
                disabled={added || saving}
                onClick={() => toggle(source)}
                className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${
                  added
                    ? 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed'
                    : isSelected
                      ? 'border-violet-400 dark:border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                      : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-medium ${added ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                    {label}
                  </span>
                  {added ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Added</span>
                  ) : (
                    isSelected && <Check className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                  )}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{example}</span>
              </button>
            )
          })}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onAdd(chosen)}
            disabled={!chosen.length || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {chosen.length ? `Add ${chosen.length}` : 'Add'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
